import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { withProcedureWraper } from "../../..";
import { getEnv } from "../../../../../AppConfigs";
import { Routes } from "../../../../../AppRoutes";
import { Icon } from "../../../../../components/icon";
import { approverType } from "../../../../../constants/itCodeRequestConstants";
import { SecurityControlCardRequestService } from "../../../../../services/company-office/SecurityControlCardService";
import { useSelector } from "../../../../../store/store";
import { ProcessContextProvider, ProcessContextType } from "../../../process-context";
import SecurityControlCardInformation from "./information";
import SecurityControlCardProgress from "./progress";
import { useQueryClient } from "@tanstack/react-query";

export type ExtraStateContext = {
  isLongTerm: boolean;
  isProvide: boolean;
  isRenew: boolean;
  isEvict: boolean;
  pickedItHead: any;
  openUpdateModal: boolean;
  setOpenUpdateModal: React.Dispatch<React.SetStateAction<boolean>>;
  reqDetail: any;
  setSercurityControlCardRequest: React.Dispatch<React.SetStateAction<any>>;
  setReqDetail: React.Dispatch<React.SetStateAction<any>>;
  onApproverChange: (selectedOptions: any) => void;
};

export const siteUrl = getEnv("REACT_APP_APPROVER_SECURITY_CONTROL_CARD_REQUEST_URL");

export const sercurityConTrolCardDetailPage: FC = withProcedureWraper(() => {
  /**
   * Selectors
   */
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const currentUserName = user?.current?.username;

  const history = useHistory();
  const { id } = useRouteMatch().params as any;

  /**
   * States
   */
  const [sercurityControlCardRequest, setSercurityControlCardRequest] = useState(null as any);
  const [comment, setComment] = useState("");
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [reqDetail, setReqDetail] = useState(null as any);
  const [approvalType, setApprovalType] = useState(0);
  const [approvalStep, setApprovalStep] = useState(approverType.approver);
  const [pickedItHead, setPickedItHead] = useState<any>(null);

  const onApproverChange = useCallback((selectedOptions: any) => {
    setPickedItHead(selectedOptions ? selectedOptions.value : "");
  }, []);

  const checkApproverType = useCallback(() => {
    const {
      eApprovedBy,
      eApprovedByITLead,
      eApprovedByITHead,
      eAssignee,
      eApprovedStatus,
      eApprovedByITLeadStatus,
      eApprovedByITHeadStatus,
      eAssigneeStatus,
    } = sercurityControlCardRequest.fields;
    const approverEmail = eApprovedBy ? eApprovedBy.split("|")[0] : "";
    const approvedByITLeadEmail = eApprovedByITLead ? eApprovedByITLead.split("|")[0] : "";
    const approvedByITHeadEmail = eApprovedByITHead ? eApprovedByITHead.split("|")[0] : "";
    const assigneeEmail = eAssignee ? eAssignee.split("|")[0] : "";

    if (currentUserName.toLowerCase() === approverEmail.toLowerCase() && !eApprovedStatus) {
      setApprovalType(approverType.approver);
      return;
    }
    if (currentUserName.toLowerCase() === approvedByITLeadEmail.toLowerCase() && !eApprovedByITLeadStatus) {
      setApprovalType(approverType.itlead);
      return;
    }
    if (currentUserName.toLowerCase() === approvedByITHeadEmail.toLowerCase() && !eApprovedByITHeadStatus) {
      setApprovalType(approverType.ithead);
      return;
    }
    if (currentUserName.toLowerCase() === assigneeEmail.toLowerCase() && !eAssigneeStatus) {
      setApprovalType(approverType.assignee);
      return;
    }
    setApprovalType(0);
  }, [currentUserName, sercurityControlCardRequest?.fields]);

  const checkApproverStep = useCallback(() => {
    const { eApprovedStatus, eApprovedByITLeadStatus, eApprovedByITHeadStatus, eAssigneeStatus } =
      sercurityControlCardRequest.fields;
    if (!eApprovedStatus) {
      setApprovalStep(approverType.approver);
      return;
    }
    if (!eApprovedByITLeadStatus) {
      setApprovalStep(approverType.itlead);
      return;
    }
    if (!eApprovedByITHeadStatus) {
      setApprovalStep(approverType.ithead);
      return;
    }
    if (!eAssigneeStatus) {
      setApprovalStep(approverType.assignee);
      return;
    }
  }, [sercurityControlCardRequest?.fields]);

  const handleUpdatePin = useCallback(
    async (isPin: boolean) => {
      try {
        if (!sercurityControlCardRequest) return;
        const pins: any[] = sercurityControlCardRequest.fields.ePins
          ? JSON.parse(sercurityControlCardRequest.fields.ePins)
          : [];
        const index = pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase());
        if (!isPin && index < 0) return;
        if (isPin && index > -1) return;
        if (!isPin && index > -1) pins.splice(index, 1);
        if (isPin && index < 0) pins.push(currentUserName?.toLowerCase());

        const fields = {
          ePins: JSON.stringify(pins),
        };

        await SecurityControlCardRequestService.updateSecurityControlCardRequest(id, fields);
        queryClient.invalidateQueries({ queryKey: ["security-control-card-requests"] });
      } catch (error) {
        console.log(error);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentUserName, id, sercurityControlCardRequest],
  );

  const isPin = useCallback(() => {
    if (!sercurityControlCardRequest) return false;
    const pins: any[] = sercurityControlCardRequest.fields.ePins
      ? JSON.parse(sercurityControlCardRequest.fields.ePins)
      : [];
    return pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase()) >= 0;
  }, [currentUserName, sercurityControlCardRequest]);

  useEffect(() => {
    SecurityControlCardRequestService.getSecurityControlCardRequest(id)
      .then((res) => {
        setSercurityControlCardRequest(res);
        // if (res.fields) {
        //   setComment(res.fields.eApprovalComments);
        // }
      })
      .catch((err) => console.log(err.message));
  }, [id]);

  useEffect(() => {
    if (sercurityControlCardRequest) {
      checkApproverType();
      checkApproverStep();
    }
  }, [checkApproverStep, checkApproverType, sercurityControlCardRequest]);

  const ctx = useMemo<ProcessContextType<ExtraStateContext>>(
    () => ({
      Request: sercurityControlCardRequest,
      setSercurityControlCardRequest,
      approvalStep,
      approvalType,
      comment,
      setComment,
      openUpdateModal,
      setOpenUpdateModal,
      reqDetail,
      setReqDetail,
      /**
       * Extra props
       */
      isLongTerm: sercurityControlCardRequest?.fields.eStage === "longTerm",
      isProvide: sercurityControlCardRequest?.fields.eSuggestion === "provide",
      isEvict: sercurityControlCardRequest?.fields.eSuggestion === "evict",
      isRenew: sercurityControlCardRequest?.fields.eSuggestion === "renew",
      pickedItHead,
      onApproverChange,
      updatePin: handleUpdatePin,
      isPin: isPin,
    }),
    [
      sercurityControlCardRequest,
      approvalStep,
      approvalType,
      comment,
      openUpdateModal,
      reqDetail,
      pickedItHead,
      onApproverChange,
      handleUpdatePin,
      isPin,
    ],
  );

  const handleGoBack = () => {
    if (history.action === "PUSH") history.goBack();
    else history.push(Routes.securityControlCardIssuance.path);
  };

  return (
    <div className="ItCodeGrantedDetailPage pd15">
      <div className="PageHead">
        <div className="title">
          <div className="btnBack" onClick={handleGoBack}>
            <Icon.ArrowLeft />
          </div>
          {"Xem Xét Yêu Cầu Duyệt"}
        </div>
      </div>

      <ProcessContextProvider context={ctx}>
        <SecurityControlCardProgress />
        <SecurityControlCardInformation />
      </ProcessContextProvider>
    </div>
  );
});
